import styled from '@emotion/styled';
import { SourceListWrapper } from '@seedlang/hoc';
import { LanguageIcon } from '@seedlang/icons';
import { pixify } from '@seedlang/utils';
import FlagButton from 'components/button/flag_button';
import Spinner from 'components/spinner';
import Text from 'components/text';
import { isFunction } from 'lodash';
import { observer } from 'mobx-react';
import React from 'react';
import { Languages } from '@seedlang/constants';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  .source-wrapper:last-child {
    border-bottom: none;
  }
  cursor: ${props => props.cursor};
`;

const SourceWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.leftAlign ? 'left' : 'center'};
  flex: 1;
  width: 100%;
  padding: ${props => props.padding};
  margin: ${props => props.margin};
  background: ${props => props.background};
  border-bottom: ${props => props.borderBottom};
  position: relative;
  &[role='button'] {
    transition: background 0.15s ease;
    &:hover, &:focus-visible { background: #F1F1F1; }
    &:active { background: #E5E5E5; }
  }
  .fa-times {
    color: #b3b3b3;
  }
`;

const FlagWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => props.flagWrapperAlignLeft ? 'flex-start' : 'center'};
  width: ${props => props.width};
  margin: ${props => props.margin};
  .icon-wrapper {
    height: 20px;
  }
`;

const TextWrapper = styled.div`
  flex: 1;
  padding: ${props => props.padding};
`;

const MoreSourcesButton = styled.div`
  margin: 5px; 
  cursor: pointer;
  font-style: italic;
  color: #676767;
  font-size: 14px;
`;

@observer
class SourceList extends React.Component {

  static defaultProps = {
    flagWrapperWidth: 25,
    hideGender: false,
    fontSize: 30,
    numberPreviewSources: 1,
  };
  languageName = (languageId) => {
    if (!languageId) return undefined;
    return Languages[languageId];
  }
  afterFlag = () => {
    if (this.props.afterFlag) {
      this.props.afterFlag();
    }
  }

  itemSources = (item) => {
    return item.sources || [item];
  }

  sourceText = (translationSource) => {
    return (
      <>
        {translationSource.source.textForUser(this.props.user)}
        {translationSource.source.displayGender && !this.props.hideGender && this.props.word.genderAbbreviation}
      </>
    );
  }

  render() {
    return (
      <Wrapper
        onClick={this.props.translations?.length > this.props.numberPreviewSources && this.props.showMoreTranslationsButton ? this.props.expandSources : this.props.onClick}
        cursor={this.props.showMoreTranslationsButton || isFunction(this.props.onClick) ? 'pointer' : null}
      >
        {
          this.props.showSpinner &&
          <Spinner />
        }
        {
          this.props.word && this.props.translations && this.props.translations.map((item, index) => {
            if (this.props.showMoreTranslationsButton && index > 0) {
              return null;
            }
            return (
              <SourceWrapper
                role={item.onPress ? 'button' : undefined}
                tabIndex={item.onPress ? 0 : undefined}
                style={item.onPress ? { cursor: 'pointer' } : undefined}
                onClick={item.onPress ? event => { event.stopPropagation(); item.onPress(); } : undefined}
                onKeyDown={item.onPress ? event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.stopPropagation();
                    item.onPress();
                  }
                } : undefined}
                key={item.id}
                borderBottom={this.props.borderBottom}
                leftAlign={this.props.leftAlign}
                translate={this.props.translate}
                className='source-wrapper'
                padding={this.props.sourcePadding}
                margin={this.props.sourceMargin}
                background={this.props.background}
              >
                {
                  this.props.showFlags &&
                  <FlagWrapper
                    width={pixify(this.props.flagWrapperWidth)}
                    flagWrapperAlignLeft={this.props.flagWrapperAlignLeft}
                    aria-label={this.languageName(item.source?.languageId)}
                  >
                    <LanguageIcon
                      margin='0 5px 0 0'
                      languageId={item.source && item.source?.languageId}
                    />
                  </FlagWrapper>
                }
                {
                  item.source &&
                  <TextWrapper
                    padding={0}
                  >
                    <Text
                      fontSize={pixify(this.props.translationFontSize(item))}
                      lineHeight={pixify(this.props.translationLineHeight(item))}
                      textAlign={this.props.leftAlign ? 'left' : ''}
                      lang={item.source?.languageId}
                    >
                      {
                        this.itemSources(item).map((translationSource, sourceIndex) => (
                          <React.Fragment
                            key={translationSource.id || sourceIndex}
                          >
                            {sourceIndex > 0 && ', '}
                            {item.onPress ? <span style={{ borderBottom: '2px solid #b5b5b5' }}>{this.sourceText(translationSource)}</span> : this.sourceText(translationSource)}
                          </React.Fragment>
                        ))
                      }
                    </Text>
                  </TextWrapper>
                }
                {
                  Boolean(this.props.centerTextInDeck && this.props.showFlags) &&
                  <FlagWrapper
                    width={pixify(this.props.flagWrapperWidth)}
                    aria-hidden="true"
                  />
                }
                {
                  !this.props.hideRetranslationIcon &&
                  <FlagButton
                    translationSource={item}
                    word={this.props.word}
                    onAfterFlag={this.afterFlag}
                  />
                }
                {
                  this.props.showFlag &&
                  <FlagWrapper
                    width={pixify(this.props.flagWrapperWidth)}
                    flagWrapperAlignLeft={this.props.flagWrapperAlignLeft}
                  >
                  </FlagWrapper>
                }
              </SourceWrapper>
            );
          })
        }
        {
          this.props.showMoreTranslationsButton &&
          <MoreSourcesButton>
            See {this.props.translations?.length - this.props.numberPreviewSources} More Translations
          </MoreSourcesButton>
        }
      </Wrapper>
    );
  }
}

export default SourceListWrapper(SourceList);
